import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Zap, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Subscription() {
  const { user, refresh } = useAuth();
  const [usage, setUsage] = useState(null);
  const [config, setConfig] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/subscription/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({ payments_enabled: false }));
  }, []);

  const isPremium = user?.plan === "premium";
  const paymentsEnabled = config?.payments_enabled === true;

  const upgrade = async () => {
    setUpgrading(true);
    try {
      const { data } = await api.post("/subscription/checkout");
      if (data.paymentPageUrl) {
        window.location.href = data.paymentPageUrl;
      } else {
        toast.error("Ödeme sayfası açılamadı");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUpgrading(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Premium aboneliğini iptal etmek istediğine emin misin?")) return;
    setCanceling(true);
    try {
      await api.post("/subscription/cancel");
      await refresh();
      toast.success("Aboneliğin iptal edildi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCanceling(false);
    }
  };

  return (
    <AppShell variant="student">
      <div className="max-w-4xl mx-auto p-5 lg:p-10" data-testid="subscription-page">
        <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Abonelik</div>
        <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
          Aboneliğin
        </h1>
        <p className="mt-2 text-zinc-600">Mevcut planın ve günlük kullanım özetin.</p>

        <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 flex items-center justify-center">
              {isPremium ? (
                <Crown className="w-5 h-5 text-[#1E3A8A]" />
              ) : (
                <Zap className="w-5 h-5 text-[#1E3A8A]" />
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm text-zinc-500">Mevcut plan</div>
              <div className="font-display text-2xl font-semibold text-zinc-900">
                {isPremium ? "Premium" : "Ücretsiz"}
              </div>
              {usage && (
                <div className="mt-4 space-y-1.5">
                  <div className="text-sm text-zinc-600">
                    Günlük limit: <span className="font-medium text-zinc-900">{usage.daily_limit} mesaj</span>
                  </div>
                  <div className="text-sm text-zinc-600">
                    Bugün kullanılan: <span className="font-medium text-zinc-900">{usage.used_today}</span>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-zinc-100 overflow-hidden">
                    <div
                      className="h-full bg-[#1E3A8A] transition-all"
                      style={{ width: `${Math.min(100, (usage.used_today / usage.daily_limit) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs text-zinc-500 mt-1">
                    Kalan: {usage.remaining_today} mesaj
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className={`rounded-2xl border ${!isPremium ? "border-[#1E3A8A]" : "border-zinc-200"} bg-white p-6`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg">Ücretsiz</h3>
              {!isPremium && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A]">Aktif</span>}
            </div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-700">
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Günde 10 AI mesajı</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> LGS · TYT · AYT sayısal dersleri</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Sohbet geçmişi</li>
            </ul>
          </div>

          <div className={`rounded-2xl border ${isPremium ? "border-[#1E3A8A]" : "border-zinc-200"} bg-white p-6`}>
            <div className="flex items-center justify-between">
              <h3 className="font-display font-semibold text-lg flex items-center gap-2">
                <Crown className="w-4 h-4 text-[#1E3A8A]" /> Premium
              </h3>
              {isPremium && <span className="text-xs px-2 py-0.5 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A]">Aktif</span>}
            </div>
            <ul className="mt-4 space-y-2 text-sm text-zinc-700">
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Yüksek günlük limit</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Öncelikli erişim</li>
              <li className="flex gap-2"><Check className="w-4 h-4 text-[#10B981] mt-0.5" /> Tam sohbet geçmişi</li>
            </ul>
            {!isPremium && (
              paymentsEnabled ? (
                <Button
                  onClick={upgrade}
                  disabled={upgrading}
                  className="mt-6 w-full rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                  data-testid="upgrade-btn"
                >
                  {upgrading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Premium'a Geç"}
                </Button>
              ) : (
                <div className="mt-6" data-testid="upgrade-coming-soon">
                  <Button
                    disabled
                    className="w-full rounded-full bg-[#1E3A8A] text-white opacity-70 cursor-not-allowed"
                  >
                    Yakında
                  </Button>
                  <p className="mt-2 text-xs text-zinc-500 text-center">
                    Premium abonelik çok yakında aktif. Ücretsiz plan ile öğrenmeye devam edebilirsin.
                  </p>
                </div>
              )
            )}
            {isPremium && (
              <Button
                onClick={cancel}
                disabled={canceling}
                variant="outline"
                className="mt-6 w-full rounded-full"
                data-testid="cancel-btn"
              >
                {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aboneliği İptal Et"}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-8 text-xs text-zinc-500 leading-relaxed">
          {paymentsEnabled ? (
            <>
              Ödemeler lisanslı ödeme kuruluşu iyzico üzerinden güvenle işlenir. Kart bilgilerin
              SuperZeka&apos;ya iletilmez. Aboneliğini istediğin zaman iptal edebilirsin;
              iptal sonrasında Ücretsiz planla devam edersin.
            </>
          ) : (
            <>
              Şu anda Ücretsiz plan tüm öğrencilere açık. Premium abonelik yakında ödeme entegrasyonuyla birlikte
              aktifleşecek — o zamana kadar tüm temel özellikleri sınırsız kullanabilirsin.
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
